/**
 * Utility functions for opening external URLs
 */

export interface OpenUrlResult {
  success: boolean;
  error?: string;
}

/**
 * AI Agent configurations
 */
export const AI_AGENTS = {
  claude: {
    name: 'Claude',
    url: 'https://claude.ai',
    displayUrl: 'claude.ai'
  },
  copilot: {
    name: 'Microsoft Copilot',
    url: 'https://copilot.cloud.microsoft',
    displayUrl: 'copilot.cloud.microsoft'
  },
  chatgpt: {
    name: 'ChatGPT',
    url: 'https://www.chatgpt.com',
    displayUrl: 'www.chatgpt.com'
  }
} as const;

export type AgentId = keyof typeof AI_AGENTS;

/**
 * Open an AI agent URL in a new window/tab
 * @param agentId The AI agent identifier
 * @returns Result indicating success or failure
 */
export function openAiAgent(agentId: AgentId): OpenUrlResult {
  try {
    const agent = AI_AGENTS[agentId];
    if (!agent) {
      return {
        success: false,
        error: `Unknown AI agent: ${agentId}`
      };
    }

    // Open in new window with specific features
    // Note: Due to browser security policies, window.open() may return null
    // even when the window opens successfully, especially with noopener
    try {
      window.open(
        agent.url,
        '_blank',
        'noopener,noreferrer,width=1200,height=800,scrollbars=yes,resizable=yes'
      );
      
      // If we get here without an exception, assume success
      // Modern browsers with noopener will return null but still open the window
      return { success: true };
    } catch (error) {
      console.error('Failed to open window:', error);
      return {
        success: false,
        error: 'Failed to open new window. Please check if popup blockers are enabled and allow popups for this site.'
      };
    }
  } catch (error) {
    console.error('Failed to open AI agent:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to open AI agent'
    };
  }
}

/**
 * Check if popup windows are likely to be blocked
 * @returns boolean indicating if popups might be blocked
 */
export function isPopupLikelyBlocked(): boolean {
  try {
    const testWindow = window.open('', '_blank', 'width=1,height=1');
    if (testWindow) {
      testWindow.close();
      return false;
    }
    return true;
  } catch {
    return true;
  }
}

/**
 * Get user-friendly instructions for enabling popups
 * @returns string with popup enabling instructions
 */
export function getPopupInstructions(): string {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (userAgent.includes('chrome')) {
    return 'In Chrome: Click the popup blocked icon in the address bar and select "Always allow popups from this site"';
  } else if (userAgent.includes('firefox')) {
    return 'In Firefox: Click the shield icon in the address bar and enable popups for this site';
  } else if (userAgent.includes('safari')) {
    return 'In Safari: Go to Safari menu > Preferences > Websites > Pop-up Windows and allow for this site';
  } else if (userAgent.includes('edge')) {
    return 'In Edge: Click the popup blocked icon in the address bar and allow popups for this site';
  }
  
  return 'Please enable popups for this site in your browser settings';
}